// src/views/parent/PABulletinDetail.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useParentService } from "../../services/parentService";
import Swal from "sweetalert2";

// ─── CARTE BULLETIN (mise en page A4, identique au rendu PDF/admin) ─────
// ⚠️ Design intentionnellement figé (couleurs, mise en page) : c'est le
// même rendu que le PDF généré côté serveur, un document officiel — il ne
// suit pas le thème Kamalpha de l'application.
function BulletinCard({ bulletin }) {
  const [imageErrors, setImageErrors] = useState({
    logoEcole: false,
    logoPays: false
  });

  const [formattedNotes, setFormattedNotes] = useState([]);

  // Formater les notes pour optimiser l'affichage
  useEffect(() => {
    if (bulletin?.notes) {
      const formatted = bulletin.notes.map(note => ({
        ...note,
        valeur: formatMoyenne(note.valeur),
        displayNote: formatMoyenneForDisplay(note.valeur)
      }));
      setFormattedNotes(formatted);
    }
  }, [bulletin]);

  const handleImageError = (type) =>
    setImageErrors((prev) => ({ ...prev, [type]: true }));

  const getImageUrl = (path) => path || null;

  const formatMoyenne = (m) => {
    if (m === null || m === undefined || m === "") return "0.00";
    const num = parseFloat(m);
    return isNaN(num) ? "0.00" : num.toFixed(2);
  };

  const formatMoyenneForDisplay = (m) => {
    if (m === null || m === undefined || m === "") return "0,00";
    const num = parseFloat(m);
    return isNaN(num) ? "0,00" : num.toFixed(2).replace('.', ',');
  };

  const getAppreciationEducateur = () => {
    return bulletin?.appreciation_educateur || "Non évalué";
  };

  const getAppreciationEcole = () => {
    return bulletin?.appreciation_ecole || "Votre enfant doit redoubler d'efforts";
  };

  const formatDateNaissance = (dateStr) => {
    if (!dateStr) return "Non renseignée";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('fr-FR');
    } catch (e) {
      return dateStr;
    }
  };

  const calculerMoyenne = () => {
    if (bulletin?.moyenne_generale !== undefined && bulletin.moyenne_generale !== null && bulletin.moyenne_generale !== "") {
      const moy = parseFloat(bulletin.moyenne_generale);
      return isNaN(moy) ? 0 : moy;
    }

    if (bulletin?.notes && bulletin.notes.length > 0) {
      const notesValides = bulletin.notes.filter(n => {
        const valeur = parseFloat(n.valeur);
        return !isNaN(valeur);
      });

      if (notesValides.length === 0) return 0;

      const somme = notesValides.reduce((acc, note) => {
        const coef = note.coefficient || 1;
        return acc + (parseFloat(note.valeur) * coef);
      }, 0);

      const totalCoef = notesValides.reduce((acc, note) => {
        return acc + (note.coefficient || 1);
      }, 0);

      return totalCoef > 0 ? somme / totalCoef : 0;
    }

    return 0;
  };

  const moyenneGenerale = calculerMoyenne();
  const moyenneFormatee = formatMoyenneForDisplay(moyenneGenerale);
  const noteMaximale = bulletin?.note_maximale || 10.00;

  const nomEcole = bulletin?.nom_ecole || "Ecole Primaire Privée KAMALPHA";
  const sloganEcole = bulletin?.slogan_ecole || "L'innovation numérique au cœur de l'éducation";
  const deviseEcole = bulletin?.devise_ecole || "DÉTERMINATION – DISCIPLINE – SUCCÈS";
  const adresseComplete = bulletin?.adresse_complete || "BP : 532 Bobo-Dioulasso – Tél : 60 01 30 10";
  const siteWeb = bulletin?.site_web || "https://www.kamalpha.org";
  const emailEcole = bulletin?.email || "centrekamalpha@gmail.com";
  const texteLogoPays = bulletin?.texte_logo_pays || "BURKINA FASO";
  const sousTexteLogoPays = bulletin?.sous_texte_logo_pays || "Unité – Progrès – Justice";

  const eleveNom = bulletin?.eleve_nom || "";
  const elevePrenom = bulletin?.eleve_prenom || "";
  const classeNom = bulletin?.classe_nom || "";
  const anneeScolaire = bulletin?.annee_scolaire || "";
  const trimestreNom = bulletin?.trimestre_nom || "";
  const sousTrimestre = bulletin?.sous_trimestre_nom 
  ? ` ${bulletin.sous_trimestre_nom}` 
  : ` ${trimestreNom}`;

  const dateEdition = bulletin?.date_generation ?
    new Date(bulletin.date_generation).toLocaleDateString('fr-FR') :
    new Date().toLocaleDateString('fr-FR');

  const matieres = formattedNotes.length > 0 ? formattedNotes : [];
  const nbMatieres = matieres.length;

  const getTableStyles = () => {
    if (nbMatieres <= 8) {
      return { fontSize: "10pt", rowPadding: "1.5mm 2mm", headerFontSize: "10pt", footerFontSize: "14pt", matiereWidth: "70%", noteWidth: "30%" };
    } else if (nbMatieres <= 12) {
      return { fontSize: "9pt", rowPadding: "1.2mm 2mm", headerFontSize: "9pt", footerFontSize: "13pt", matiereWidth: "65%", noteWidth: "35%" };
    } else if (nbMatieres <= 16) {
      return { fontSize: "8.5pt", rowPadding: "1mm 1.5mm", headerFontSize: "8.5pt", footerFontSize: "12pt", matiereWidth: "60%", noteWidth: "40%" };
    } else {
      return { fontSize: "8pt", rowPadding: "0.8mm 1.2mm", headerFontSize: "8pt", footerFontSize: "11pt", matiereWidth: "55%", noteWidth: "45%" };
    }
  };

  const getAppreciationStyles = () => {
    if (nbMatieres <= 12) return { fontSize: "9pt", minHeight: "25mm" };
    if (nbMatieres <= 16) return { fontSize: "8.5pt", minHeight: "20mm" };
    return { fontSize: "8pt", minHeight: "18mm" };
  };

  const getMoyenneVirtuelleStyles = () => {
    if (nbMatieres <= 12) return { width: "40mm", titleFontSize: "8pt", moyenneFontSize: "16pt" };
    if (nbMatieres <= 16) return { width: "35mm", titleFontSize: "7.5pt", moyenneFontSize: "14pt" };
    return { width: "30mm", titleFontSize: "7pt", moyenneFontSize: "13pt" };
  };

  const tableStyles = getTableStyles();
  const appreciationStyles = getAppreciationStyles();
  const moyenneVirtuelleStyles = getMoyenneVirtuelleStyles();

  const printStyle = {
    card: {
      width: "210mm",
      minHeight: "297mm",
      margin: "0 auto",
      padding: "10mm",
      backgroundColor: "#FFFFFF",
      fontFamily: "'Times New Roman', 'Roboto', serif",
      color: "#000000",
      position: "relative",
      boxSizing: "border-box",
      border: "none",
      breakInside: "avoid",
      pageBreakInside: "avoid"
    },
    tableHeader: {
      backgroundColor: "#0D9488",
      color: "#FFFFFF",
      fontWeight: "bold"
    },
  };

  return (
    <div style={printStyle.card} className="bulletin-card" data-pdf-page="true">
      <header style={{ borderBottom: "2px dashed #404242b2", paddingBottom: "4mm", marginBottom: "6mm" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "5mm" }}>
          <div style={{ width: "25mm" }}>
            <img
              src={getImageUrl(bulletin?.logo_ecole_url)}
              alt="Logo École"
              style={{ width: "25mm", height: "25mm", objectFit: "contain" }}
              onError={() => handleImageError('logoEcole')}
            />
          </div>

          <div style={{ flex: 1, textAlign: "left", lineHeight: "1.3" }}>
            <h1 style={{ fontSize: "14pt", fontWeight: "bold", margin: 0, textTransform: "uppercase" }}>
              {nomEcole}
            </h1>
            <p style={{ fontSize: "10pt", margin: "1mm 0" }}>{sloganEcole}</p>
            <p style={{ fontSize: "9pt", margin: 0 }}>{adresseComplete}</p>
            <p style={{ fontSize: "9pt", margin: 0 }}>{siteWeb}</p>
          </div>

          <div style={{ width: "35mm", textAlign: "center", lineHeight: "1.2" }}>
            <div style={{ fontWeight: "bold", fontSize: "9pt" }}>{texteLogoPays}</div>
            <div style={{ fontSize: "8pt", fontStyle: "italic", marginBottom: "1mm" }}>{sousTexteLogoPays}</div>
            <img
              src={getImageUrl(bulletin?.logo_pays_url)}
              alt="Armoiries Burkina Faso"
              style={{ width: "22mm", height: "22mm", objectFit: "contain" }}
              onError={() => handleImageError('logoPays')}
            />
          </div>
        </div>

        <div style={{ fontSize: "9pt", fontWeight: "bold", color: "#0D9488", letterSpacing: "0.4mm", marginTop: "2mm" }}>
          {deviseEcole}
        </div>
      </header>

      <div style={{
        width: "100%", textAlign: "center", marginTop: "1mm",
        marginBottom: nbMatieres > 12 ? "3mm" : "5mm",
        backgroundColor: "#0D9488", color: "#FFFFFF", fontWeight: "bold",
        fontSize: nbMatieres > 16 ? "12pt" : "14pt", textTransform: "uppercase",
        padding: nbMatieres > 16 ? "1.5mm 0" : "2mm 0",
        border: "1px solid #0A7A6F", lineHeight: "1.2", boxSizing: "border-box"
      }}>
        Bulletin des notes du {trimestreNom}
      </div>

      <div style={{
        backgroundColor: "#F8F9FA", padding: nbMatieres > 16 ? "2mm" : "3mm",
        borderRadius: "1mm", marginBottom: nbMatieres > 12 ? "3mm" : "5mm",
        fontSize: nbMatieres > 16 ? "8pt" : "9pt", border: "1px solid #DEE2E6"
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: "2mm", columnGap: "6mm" }}>
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Nom:</strong>
            <span style={{ fontWeight: "bold" }}>{eleveNom}</span>
          </div>
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Année scolaire:</strong>
            <span style={{ fontWeight: "bold" }}>{anneeScolaire}</span>
          </div>
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Prénom(s):</strong>
            <span style={{ fontWeight: "bold" }}>{elevePrenom}</span>
          </div>
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Classe:</strong>
            <span style={{ fontWeight: "bold" }}>{classeNom}</span>
          </div>
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Né(e) le:</strong>
            <span style={{ fontWeight: "bold" }}>{formatDateNaissance(bulletin?.eleve_date_naissance)}</span>
          </div>
          <div style={{ display: "flex", gap: "2mm", alignItems: "center" }}>
            <strong>Période scolaire:</strong>
            <span style={{ fontWeight: "bold" }}>{trimestreNom}</span>
          </div>
        </div>
      </div>

      <div style={{ borderBottom: "2px dashed #404242b2", margin: "4mm 0" }}></div>

      <div style={{ display: "flex", alignItems: "flex-start", gap: nbMatieres > 16 ? "4mm" : "6mm", marginBottom: nbMatieres > 12 ? "3mm" : "5mm" }}>
        <div style={{ flexGrow: 1 }}>
          <div style={{ backgroundColor: "#FFFFFF", overflow: "hidden", border: "1px solid #CCCCCC" }}>
            <table style={{ width: "100%", fontSize: tableStyles.fontSize, borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead style={printStyle.tableHeader}>
                <tr>
                  <th style={{
                    padding: "2mm", textAlign: "left", fontWeight: "bold", color: "#FFFFFF",
                    textTransform: "uppercase", fontSize: tableStyles.headerFontSize,
                    borderRight: "1px solid #0A7A6F", width: tableStyles.matiereWidth
                  }}>
                    Matières
                  </th>
                  <th style={{
                    padding: "2mm", textAlign: "right", fontWeight: "bold", color: "#FFFFFF",
                    textTransform: "uppercase", fontSize: tableStyles.headerFontSize, width: tableStyles.noteWidth
                  }}>
                    Note / {noteMaximale}
                  </th>
                </tr>
              </thead>
              <tbody>
                {matieres.map((note, index) => (
                  <tr key={index} style={{
                    backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F8F9FA",
                    borderTop: "1px solid #E9ECEF"
                  }}>
                    <td style={{
                      padding: tableStyles.rowPadding, color: "#000000", fontSize: tableStyles.fontSize,
                      borderRight: "1px solid #E9ECEF", lineHeight: "1.2",
                      wordBreak: nbMatieres > 16 ? "break-word" : "normal", width: tableStyles.matiereWidth
                    }}>
                      {note.matiere_nom}
                    </td>
                    <td style={{
                      padding: tableStyles.rowPadding, textAlign: "right",
                      fontFamily: "'Times New Roman', 'Roboto', serif", fontWeight: "bold",
                      fontVariantNumeric: "normal", color: "#000000",
                      fontSize: tableStyles.fontSize, lineHeight: "1.2", width: tableStyles.noteWidth
                    }}>
                      {note.displayNote}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: "2mm", display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: "12pt", textTransform: "uppercase" }}>
            <span style={{ backgroundColor: "#0D9488", color: "#FFFFFF", padding: "2px 4px", borderRadius: "2px" }}>
              Moyenne Générale
            </span>
            <span style={{ color: "#000000" }}>
              {moyenneFormatee} / {noteMaximale}
            </span>
          </div>
        </div>

        <div style={{ flexShrink: 0, width: `${parseFloat(moyenneVirtuelleStyles.width) + 20}mm` }}>
          <div style={{
            backgroundColor: "#0D9488", color: "#FFFFFF", padding: "4mm",
            textAlign: "center", border: "1px solid #0D9488", borderRadius: "4px 4px 0 0"
          }}>
            <h4 style={{
              fontSize: moyenneVirtuelleStyles.titleFontSize, fontWeight: "bold",
              textTransform: "uppercase", margin: 0, lineHeight: "1.2"
            }}>
              Moyenne du {sousTrimestre}
            </h4>
          </div>
          <div style={{ border: "1px solid #CCCCCC", borderTop: "none", textAlign: "center", padding: "4mm", borderRadius: "0 0 4px 4px" }}>
            <p style={{
              fontSize: `${parseFloat(moyenneVirtuelleStyles.moyenneFontSize) + 6}pt`,
              fontWeight: "bold", color: "#0D9488", margin: 0, lineHeight: "1.2"
            }}>
              {moyenneFormatee}
            </p>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: nbMatieres > 16 ? "3mm" : "4mm", marginBottom: "4mm" }}>
        <div style={{ backgroundColor: "#FFFFFF", padding: "3mm", border: "1px solid #CCCCCC", borderTop: "3px solid #0D9488", minHeight: appreciationStyles.minHeight }}>
          <h4 style={{
            fontWeight: "bold", fontSize: nbMatieres > 16 ? "9pt" : "10pt", color: "#000000",
            margin: "0 0 1mm 0", paddingBottom: "0.5mm", borderBottom: "1px solid #E9ECEF", lineHeight: "1.2"
          }}>
            Appréciation de l'éducateur
          </h4>
          <p style={{ color: "#333333", fontSize: appreciationStyles.fontSize, margin: 0, lineHeight: "1.3" }}>
            {getAppreciationEducateur()}
          </p>
        </div>

        <div style={{ backgroundColor: "#FFFFFF", padding: "3mm", border: "1px solid #CCCCCC", borderTop: "3px solid #0D9488", minHeight: appreciationStyles.minHeight }}>
          <h4 style={{
            fontWeight: "bold", fontSize: nbMatieres > 16 ? "9pt" : "10pt", color: "#000000",
            margin: "0 0 1mm 0", paddingBottom: "0.5mm", borderBottom: "1px solid #E9ECEF", lineHeight: "1.2"
          }}>
            Appréciation de l'école
          </h4>
          <p style={{ color: "#333333", fontSize: appreciationStyles.fontSize, margin: 0, lineHeight: "1.3" }}>
            {getAppreciationEcole()}
          </p>
        </div>
      </div>

      <footer style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        marginTop: nbMatieres > 12 ? "2mm" : "5mm", paddingTop: "2mm",
        borderTop: "1px solid #0D9488", position: "absolute",
        bottom: "10mm", left: "10mm", right: "10mm", fontSize: "8pt", lineHeight: "1.2"
      }}>
        <div style={{ textAlign: "left", color: "#0D9488", fontWeight: "bold" }}>
          {sloganEcole}
        </div>

        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: "8pt", color: "#666666", margin: "0 0 0.5mm 0", fontStyle: "italic" }}>
            Document officiel de l'école
          </p>
          <p style={{ fontSize: "7pt", color: "#999999", margin: 0 }}>
            Édité le {dateEdition}
          </p>
        </div>

        <div style={{ textAlign: "right", color: "#0D9488" }}>
          <p style={{ margin: 0, fontWeight: "bold" }}>{siteWeb}</p>
          <p style={{ margin: 0 }}>{emailEcole}</p>
        </div>
      </footer>
    </div>
  );
}

// ─── APPBAR (toujours visible) ────────────────────────────────────────
function BulletinAppBar({ navigate, generatingPDF, onDownloadPDF, disablePDF }) {
  return (
    <div className="pa-bulletin-appbar">
      <button className="back-btn" onClick={() => navigate(-1)}>
        <i className="ti ti-arrow-left" />
        <span>Retour</span>
      </button>
      <h2 className="title">Bulletin scolaire</h2>

      <button
        className="pdf-btn"
        onClick={onDownloadPDF}
        disabled
      >
        {generatingPDF ? (
          <span key="spinner" className="spinner" />
        ) : (
          <i key="icon" className="ti ti-file-pdf" />
        )}
        <span>PDF</span>
      </button>
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────
function BulletinDetailStyles() {
  return (
    <style>{`
      .pa-bulletin-detail-page {
        min-height: 100vh;
        background: var(--s2);
        padding: 12px;
      }

      .pa-bulletin-appbar {
        background: var(--pr);
        padding: 12px 16px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-radius: 12px;
        margin-bottom: 12px;
        position: sticky;
        top: 0;
        z-index: 100;
      }

      .pa-bulletin-appbar .back-btn {
        background: none;
        border: none;
        cursor: pointer;
        color: #fff;
        display: flex;
        align-items: center;
        gap: 6px;
        font-family: inherit;
        font-size: 14px;
        font-weight: 500;
      }

      .pa-bulletin-appbar .back-btn:hover { opacity: 0.8; }

      .pa-bulletin-appbar .title {
        font-family: inherit;
        color: #fff;
        font-size: 16px;
        font-weight: 700;
        margin: 0;
      }

      .pa-bulletin-appbar .pdf-btn {
        background: rgba(255,255,255,0.2);
        border: none;
        border-radius: 8px;
        padding: 8px 12px;
        cursor: pointer;
        color: #fff;
        display: flex;
        align-items: center;
        gap: 6px;
        font-family: inherit;
        font-size: 13px;
        transition: background 0.2s;
      }

      .pa-bulletin-appbar .pdf-btn:hover { background: rgba(255,255,255,0.3); }
      .pa-bulletin-appbar .pdf-btn:disabled { opacity: 0.5; cursor: default; }

      .pa-bulletin-appbar .spinner {
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 2px solid #fff;
        border-top-color: transparent;
        border-radius: 50%;
        animation: pa-spin 0.8s linear infinite;
      }

      @keyframes pa-spin { to { transform: rotate(360deg); } }

      .pa-bulletin-scroll {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }

      .pa-bulletin-scroll > div {
        box-shadow: 0 4px 20px rgba(0,0,0,0.10);
      }

      /* Loader intégré dans la zone de contenu */
      .pa-bulletin-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 80px 20px;
        gap: 14px;
      }

      .pa-bulletin-loading .spinner {
        width: 44px;
        height: 44px;
        border: 4px solid var(--pr-bg, #e5eef7);
        border-top-color: var(--pr);
        border-radius: 50%;
        animation: pa-spin 0.8s linear infinite;
      }

      .pa-bulletin-loading span {
        font-family: 'Inter', sans-serif;
        font-size: 13px;
        color: var(--m);
      }

      /* Carte d'erreur */
      .pa-bulletin-error {
        background: #fff;
        border-radius: 20px;
        padding: 40px 20px;
        text-align: center;
        box-shadow: 0 4px 18px rgba(0,0,0,0.08);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        margin: 16px auto;
        max-width: 480px;
      }

      .pa-bulletin-error i.alert-icon {
        font-size: 48px;
        color: var(--rd);
      }

      .pa-bulletin-error h2 {
        font-family: 'Poppins', sans-serif;
        font-size: 17px;
        font-weight: 600;
        color: var(--t);
        margin: 4px 0 0;
      }

      .pa-bulletin-error p {
        font-family: 'Inter', sans-serif;
        font-size: 13px;
        color: var(--m);
        margin: 0 0 12px;
      }

      .pa-bulletin-error .actions-row {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        justify-content: center;
      }

      .pa-bulletin-error .btn {
        border: none;
        border-radius: 30px;
        padding: 10px 22px;
        cursor: pointer;
        font-family: 'Inter', sans-serif;
        font-size: 13px;
        font-weight: 600;
        color: #fff;
        background: var(--pr);
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: opacity 0.2s;
      }

      .pa-bulletin-error .btn:hover { opacity: 0.9; }
      .pa-bulletin-error .btn.btn-secondary {
        background: var(--b2);
        color: var(--t);
      }

      @media (max-width: 900px) {
        /* Le bulletin est au format A4 fixe (210mm) — on le fait défiler
           horizontalement sur petit écran plutôt que de le déformer. */
        .pa-bulletin-scroll {
          display: flex;
          justify-content: flex-start;
        }
      }
    `}</style>
  );
}

// ─── PAGE PRINCIPALE ──────────────────────────────────────────────────
export default function PABulletinDetail({ slug }) {
  const { bulletinId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getBulletinDetail, telechargerBulletinPDF } = useParentService();

  const [bulletin, setBulletin] = useState(location.state?.bulletin || null);
  const [loading, setLoading] = useState(!location.state?.bulletin);
  const [error, setError] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  const hasFetchedRef = useRef(false);

  // ─── CHARGEMENT DU BULLETIN ─────────────────────────────────────────
  const loadBulletin = useCallback(async () => {
    // Pas de bulletinId ou slug → erreur explicite
    if (!bulletinId || !slug) {
      setError("Identifiant du bulletin manquant.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getBulletinDetail(slug, bulletinId);
      setBulletin(data);
    } catch (err) {
      setError("Impossible de charger le bulletin.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulletinId, slug]);

  useEffect(() => {
    // Si on a déjà le bulletin via location.state, pas besoin de fetch
    if (bulletin) {
      setLoading(false);
      return;
    }
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    loadBulletin();
  }, [bulletin, loadBulletin]);

  // ─── TÉLÉCHARGEMENT PDF ─────────────────────────────────────────────
  const handleDownloadPDF = async () => {
    if (!bulletinId || generatingPDF) return;
    setGeneratingPDF(true);
    try {
      const blob = await telechargerBulletinPDF(slug, bulletinId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const nomFichier = bulletin
        ? `bulletin_${bulletin.eleve_prenom || ""}_${bulletin.eleve_nom || ""}`.trim()
        : `bulletin_${bulletinId}`;
      a.download = `${nomFichier}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: "Bulletin téléchargé",
        text: "Le PDF a été téléchargé avec succès",
        toast: true,
        timer: 2000,
        showConfirmButton: false,
        position: "top-right",
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de générer le PDF",
        confirmButtonColor: "var(--pr)",
      });
    } finally {
      setGeneratingPDF(false);
    }
  };

  // ─── RENDU ───────────────────────────────────────────────────────────
  return (
    <>
      <BulletinDetailStyles />

      {/* translate="no" : empêche la traduction automatique du navigateur
          de modifier le DOM (cause de l'erreur removeChild) */}
      <div className="pa-bulletin-detail-page" translate="no">
        {/* AppBar TOUJOURS affichée (Retour + titre + PDF) */}
        <BulletinAppBar
          navigate={navigate}
          generatingPDF={generatingPDF}
          onDownloadPDF={handleDownloadPDF}
          disablePDF={!bulletin || loading || !!error}
        />

        {loading ? (
          /* ── ÉTAT CHARGEMENT (sous l'AppBar) ── */
          <div className="pa-bulletin-loading">
            <div className="spinner" />
            <span>Chargement du bulletin…</span>
          </div>
        ) : error || !bulletin ? (
          /* ── ÉTAT ERREUR (sous l'AppBar) ── */
          <div className="pa-bulletin-error">
            <i className="ti ti-alert-circle alert-icon" />
            <h2>Erreur de chargement</h2>
            <p>{error || "Bulletin non trouvé"}</p>
            <div className="actions-row">
              <button className="btn" onClick={loadBulletin}>
                <i className="ti ti-refresh" />
                Réessayer
              </button>
              <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                <i className="ti ti-arrow-left" />
                Retour
              </button>
            </div>
          </div>
        ) : (
          /* ── BULLETIN ── */
          <div className="pa-bulletin-scroll">
            <BulletinCard bulletin={bulletin} />
          </div>
        )}
      </div>
    </>
  );
}